import { useState } from "react";
import { Alert, Badge, Button, TextField } from "@printtat/ui";
import { Copy, ShieldCheck, ShieldX } from "lucide-react";
import type {
  MfaConfirmResponse,
  MfaSetupResponse,
} from "@printtat/shared";
import { apiJson } from "@/lib/apiFetch";

interface MfaSetupFormProps {
  totpEnabled: boolean;
  onChange: () => void; // Caller refetches user to pick up totpEnabled change
}

type Stage =
  | { kind: "idle" }
  | { kind: "setting-up"; setup: MfaSetupResponse; code: string }
  | { kind: "showing-codes"; codes: string[] }
  | { kind: "disabling"; code: string };

export const MfaSetupForm = ({ totpEnabled, onChange }: MfaSetupFormProps) => {
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const beginSetup = async () => {
    setError(null);
    setBusy(true);
    try {
      const setup = await apiJson<MfaSetupResponse>("/auth/2fa/setup", {
        method: "POST",
      });
      setStage({ kind: "setting-up", setup, code: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start MFA setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (stage.kind !== "setting-up") return;
    setError(null);
    setBusy(true);
    try {
      const result = await apiJson<MfaConfirmResponse>("/auth/2fa/confirm", {
        method: "POST",
        body: { code: stage.code },
      });
      setStage({ kind: "showing-codes", codes: result.backupCodes });
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const beginDisable = () => {
    setError(null);
    setStage({ kind: "disabling", code: "" });
  };

  const submitDisable = async () => {
    if (stage.kind !== "disabling") return;
    setError(null);
    setBusy(true);
    try {
      await apiJson("/auth/2fa/disable", {
        method: "POST",
        body: { code: stage.code },
      });
      setStage({ kind: "idle" });
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setStage({ kind: "idle" });
    setError(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {totpEnabled ? (
            <ShieldCheck className="w-4 h-4 text-green-600" />
          ) : (
            <ShieldX className="w-4 h-4 text-gray-400" />
          )}
          <Badge color={totpEnabled ? "green" : "gray"} variant="soft">
            {totpEnabled ? "Enabled" : "Not Enabled"}
          </Badge>
        </div>
        {stage.kind === "idle" && !totpEnabled && (
          <Button size="sm" onClick={beginSetup} disabled={busy}>
            Enable 2FA
          </Button>
        )}
        {stage.kind === "idle" && totpEnabled && (
          <Button size="sm" variant="outline" onClick={beginDisable}>
            Disable 2FA
          </Button>
        )}
      </div>

      {stage.kind === "setting-up" && (
        <div className="space-y-3 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            Scan this QR with any TOTP authenticator app, then enter the
            6-digit code it shows.
          </p>
          <div className="flex flex-col items-center gap-3">
            <img
              src={stage.setup.qrCodeDataUrl}
              alt="2FA QR code"
              className="w-48 h-48 border border-gray-200 rounded"
            />
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>Or enter manually:</span>
              <code className="bg-gray-100 px-2 py-1 rounded font-mono">
                {stage.setup.secret}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(stage.setup.secret)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Copy secret"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <TextField
            value={stage.code}
            onChange={(e) =>
              setStage({ ...stage, code: e.target.value.replace(/\D/g, "").slice(0, 6) })
            }
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
          />
          {error && <Alert variant="error">{error}</Alert>}
          <div className="flex gap-2">
            <Button
              onClick={confirmSetup}
              disabled={busy || stage.code.length !== 6}
            >
              {busy ? "Verifying…" : "Confirm"}
            </Button>
            <Button variant="outline" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {stage.kind === "showing-codes" && (
        <div className="space-y-3 border border-amber-200 bg-amber-50 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-900">
            Save these backup codes
          </p>
          <p className="text-xs text-amber-800">
            Each code can be used once if you lose access to your authenticator.
            They will NOT be shown again — store them somewhere safe.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white rounded p-3 border border-amber-200">
            {stage.codes.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigator.clipboard.writeText(stage.codes.join("\n"))
              }
            >
              <Copy size={14} className="mr-1.5" />
              Copy all
            </Button>
            <Button size="sm" onClick={() => setStage({ kind: "idle" })}>
              I&apos;ve saved them
            </Button>
          </div>
        </div>
      )}

      {stage.kind === "disabling" && (
        <div className="space-y-3 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            Enter a 6-digit code from your authenticator (or a backup code) to
            confirm disabling 2FA.
          </p>
          <TextField
            value={stage.code}
            onChange={(e) => setStage({ ...stage, code: e.target.value.trim() })}
            placeholder="123456 or xxxx-xxxx"
            autoComplete="one-time-code"
            autoFocus
          />
          {error && <Alert variant="error">{error}</Alert>}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={submitDisable}
              disabled={busy || stage.code.length < 6}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              {busy ? "Disabling…" : "Disable 2FA"}
            </Button>
            <Button variant="outline" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
